import { defineStore } from 'pinia'
import { type ComputedRef, type Ref, computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { type ToastOptions, useToastStore } from '@/components/Toaster/store'
import { useTr } from '@/composables/use-tr'
import { PWA_APP_CLIDS } from '@/constants/general'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import {
    isBrowserFirefox,
    isBrowserIOSChrome,
    isBrowserIOSSafari,
    isDesktopChrome,
    isDesktopEdge,
    isDesktopSafari,
} from '@/utils/device'
import { leaveFullscreen } from '@/utils/helpers'

import ChromeDesktopSVG from './guides/chrome-desktop.svg'

const PWA_INSTALL_CONFIG = {
    MIN_VISITS: 2,
    MAX_DISPLAY_COUNT: 100,
    DISPLAY_DELAY_HOURS: 24 * 7,
} as const

const GAME_INSTALL_SOURCE = 'game-bridge'
// transient user activation lifespan: prompt() is refused after it
const WEB_APP_PROMPT_WAIT_MS = 5000

const msStoreGameIds: Record<string, string> = {
    'moto-x3m': '9pfc45g1xhp7',
    'hidden-object-clues-and-mysteries': '9ndwwpl265t4',
    'fashion-princess--dress-up-for-girls-e00c-1': '9mvfcjf74zfn',
    'age-of-tanks-warriors-td-war': '9nxv05zxpkq7',
    'home-design-decorate-house': '9n403rx37m6x',
    'sprunki-world-online-rp--play-with-friends': '9mx9nlhd6xmm',
    'speed-per-click-obby': '9pbp2j1pt8rd',
    'mr-racer-car-racing': '9n08dpggwvqh',
    'hazmob-fps-online-shooter': '9n7qs1kn0v8l',
    'motorcycle-racer-road-mayhem': '9mwr95hfg2zg',
    'playground-man-ragdoll-show': '9nj10wf288jp',
}

export interface BeforeInstallPromptEvent extends Event {
    platforms: string[]
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export const usePWAInstallStore = defineStore(
    'pwa_install',
    () => {
        const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
        // set boolean flag to prevent re-rendering components
        const hasDeferredPrompt = ref<boolean>(false)
        const toastStore = useToastStore()
        const layoutStore = useLayoutStore()
        const loggerStore = useLoggerStore()
        const tr = useTr()

        const displayCount = ref(0)
        const showingNativePWA = ref(false)
        const lastDisplayTime = ref(0)
        const installed = ref(false)
        const installedGames: Ref<Record<string, boolean>> = ref({})
        const sessions = ref(0)
        const initialized = ref(false)
        const isDisplaying = ref(false)
        const pwaHasShowed = ref(false)
        const webAppManifestGame = ref<string>()
        const verifiedInstalls: Ref<Record<string, boolean>> = ref({})
        const homeScreenRewards: Ref<Record<string, boolean>> = ref({})
        let onWebAppPrompt: ((event?: BeforeInstallPromptEvent) => void) | undefined

        const route = useRoute()
        const isGamePage = computed(() => route.name === ROUTE_NAMES.GAME_PAGE)
        const appStore = useAppStore()
        const isDesktop = computed(() => appStore.platform === 'desktop')
        const isNativeAppManifest = computed(
            () => appStore.platform === 'android' && appStore.isPortalEnv && !appStore.isAppEnv,
        )

        const hru: ComputedRef<string | undefined> = computed(() => {
            return route.params.game as string
        })

        const checkInstalled = computed(() => {
            if (isGamePage.value && hru.value) {
                if (installedGames.value[hru.value]) {
                    return true
                }
            } else {
                return installed.value
            }
            return false
        })

        const setInstalled = () => {
            if (isGamePage.value && hru.value) {
                installedGames.value = { ...installedGames.value, [hru.value]: true }
            } else {
                installed.value = true
            }
        }

        const markInstallVerified = () => {
            if (isGamePage.value && hru.value) {
                verifiedInstalls.value = { ...verifiedInstalls.value, [hru.value]: true }
            }
        }

        function claimHomeScreenReward(userId: string) {
            const key = `${userId}:${hru.value}`
            if (!hru.value || !verifiedInstalls.value[hru.value] || homeScreenRewards.value[key]) {
                return false
            }
            homeScreenRewards.value = { ...homeScreenRewards.value, [key]: true }
            return true
        }

        const shouldShowPrompt = computed(() => {
            if (!isDesktop.value) {
                return false
            }

            if (checkInstalled.value) {
                return false
            }

            if (layoutStore.fullScreen) {
                return false
            }

            if (isBrowserFirefox()) {
                return false
            }

            if (sessions.value < PWA_INSTALL_CONFIG.MIN_VISITS) {
                return false
            }

            if (displayCount.value >= PWA_INSTALL_CONFIG.MAX_DISPLAY_COUNT) {
                return false
            }

            if (Date.now() - lastDisplayTime.value < PWA_INSTALL_CONFIG.DISPLAY_DELAY_HOURS * 60 * 60 * 1000) {
                return false
            }

            return true
        })

        const updateInstallPWAConfig = () => {
            lastDisplayTime.value = Date.now()
            displayCount.value += 1
        }

        const handleOptOut = (force = false) => {
            isDisplaying.value = false
            // do not count for game install pwa as it shows very often
            if (!force) {
                updateInstallPWAConfig()
            }
        }

        const handleOnShow = (force = false) => {
            loggerStore.logInterfaceEvents({
                interface_target: 'pwa_portal_install_prompt_shown_toast',
                eventName: 'interface_show',
            })
            if (!force) {
                updateInstallPWAConfig()
            }
        }

        let nativePromptSource = ''

        const handleInstalled = () => {
            loggerStore.logEvent(
                {
                    event: 'custom_event',
                    eventName: 'pwa_native_install_accepted',
                    dimension1: nativePromptSource || 'browser-ui',
                },
                { flushImmediately: true },
            )
            nativePromptSource = ''
            setInstalled()
            markInstallVerified()
            deferredPrompt.value = null
            updateInstallPWAConfig()
            const startURL = document.querySelector<HTMLLinkElement>('link[rel=manifest]')?.dataset.start_url
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            // Chrome transparently moves current runtime into standalone mode, but it doesn't make it PWA until we reload it with proper URL with clid
            if (startURL && isStandalone) {
                location.href = startURL
            }
        }

        const handleAccepted = () => {
            if (deferredPrompt.value) {
                triggerNativePrompt('pwa-prompt')
            }

            // We show button only for native prompt atm
            // else {
            //     handleInstalled()
            // }

            isDisplaying.value = false
        }

        const triggerNativePrompt = async (source: string) => {
            if (deferredPrompt.value) {
                try {
                    showingNativePWA.value = true
                    nativePromptSource = source
                    await deferredPrompt.value.prompt()

                    loggerStore.logInterfaceEvents({
                        interface_target: 'pwa_native_install_prompt_shown',
                        eventName: 'interface_show',
                    })
                    const { outcome } = await deferredPrompt.value.userChoice

                    if (outcome !== 'accepted') {
                        nativePromptSource = ''
                        handleOptOut()
                    }
                    showingNativePWA.value = false
                    pwaHasShowed.value = true
                    return outcome === 'accepted'
                } catch {
                    nativePromptSource = ''
                    showingNativePWA.value = false
                    handleOptOut()
                }
            }
            return false
        }

        const showPWAInstallPrompt = async (settings: {
            trigger: string
            manual?: boolean
            force?: boolean
            showTimeoutMs?: number
            isFullScreenAllowed?: boolean
        }) => {
            const { trigger, manual, force, showTimeoutMs, isFullScreenAllowed } = settings
            if (checkInstalled.value || isBrowserFirefox() || appStore.isAppEnv) {
                return
            }
            if (!force && !shouldShowPrompt.value) {
                return
            }

            // loading animations when showing the toast
            let animation, image
            if (isDesktopChrome() || isDesktopEdge()) {
                image = ChromeDesktopSVG
            } else if (isBrowserIOSSafari() || isBrowserIOSChrome()) {
                animation = await import('./guides/safari.json').then((module) => module.default)
            } else if (isDesktopSafari()) {
                animation = await import('./guides/safari-desktop.json').then((module) => module.default)
            } else {
                animation = await import('./guides/chromium.json').then((module) => module.default)
            }
            const description = isDesktopSafari()
                ? tr('toast-pwa.description-safari-desktop')
                : tr('toast-pwa.description')
            const buttonText = hasDeferredPrompt.value ? tr('toast-pwa.button_text') : undefined

            isDisplaying.value = true
            pwaHasShowed.value = true

            const params: ToastOptions = {
                type: 'pwa-prompt',
                trigger,
                manual,
                title: tr('toast-pwa.title'),
                description,
                animation,
                image,
                buttonText,
                showTimeoutMs,
                weight: 1,
                imageStyles: { height: '120rem' },
                onAccept: handleAccepted,
                onClose: () => handleOptOut(force),
                onShow: () => handleOnShow(force),
                isFullScreenAllowed: isFullScreenAllowed ?? force,
            }
            toastStore.showToast(params)
        }

        const showPWAGameInstallPrompt = (gamePic: string | undefined) => {
            if (checkInstalled.value || isBrowserFirefox()) {
                return
            }
            const image = gamePic || ChromeDesktopSVG // fallback
            isDisplaying.value = true
            pwaHasShowed.value = true
            const params: ToastOptions = {
                type: 'pwa-prompt-game',
                trigger: 'game-page',
                title: tr('toast-pwa.game_title'),
                description: tr('toast-pwa.game_description'),
                image,
                showTimeoutMs: 40_000, // 40 sec
                buttonText: tr('toast-pwa.button_game_text'),
                weight: 2,
                onAccept: () => {
                    triggerNativePrompt('pwa-prompt-game')
                    isDisplaying.value = false
                },
                onClose: () => {
                    isDisplaying.value = false
                },
                onShow: () => {
                    loggerStore.logInterfaceEvents({
                        interface_target: 'pwa_portal_install_game_prompt_shown_toast',
                        eventName: 'interface_show',
                    })
                },
            }

            toastStore.showToast(params)
        }

        const forceHidePWAInstallPrompt = () => {
            if (toastStore.toast?.type === 'pwa-prompt' || toastStore.toast?.type === 'pwa-prompt-game') {
                toastStore.closeToast('replaced')
            }
        }

        async function install() {
            if (deferredPrompt.value) {
                await triggerNativePrompt('install-button')
            } else {
                await showPWAInstallPrompt({ trigger: 'install-button', manual: true, force: true })
            }
        }

        function init() {
            if (initialized.value) {
                return
            }

            initialized.value = true
            sessions.value += 1

            if (
                new URLSearchParams(location.search).get(SSR_QUERY.CLID) === PWA_APP_CLIDS.pwa &&
                (isDesktop.value
                    ? window.matchMedia('(display-mode: standalone)').matches
                    : !window.matchMedia('(display-mode: browser)').matches)
            ) {
                markInstallVerified()
            }

            if (appStore.isAppEnv) {
                setInstalled()
                return
            }

            window.addEventListener('beforeinstallprompt', (e: Event) => {
                const event = e as BeforeInstallPromptEvent
                if (isNativeAppManifest.value && !event.platforms.includes('play')) {
                    e.preventDefault()
                    onWebAppPrompt?.(event)
                } else if (isNativeAppManifest.value) {
                    //when we are in android, we know that manifest is for Google Play
                    loggerStore.logInterfaceEvents({
                        interface_target: 'native_app_smart_install_shown_android',
                        eventName: 'interface_show',
                    })
                } else {
                    e.preventDefault()
                    deferredPrompt.value = event
                    hasDeferredPrompt.value = true
                }
            })

            window.addEventListener('appinstalled', handleInstalled)
        }

        async function onInstallPWA(source = 'install-button') {
            if (hasDeferredPrompt.value) {
                forceHidePWAInstallPrompt()
                return triggerNativePrompt(source)
            }
            await showPWAInstallPrompt({ trigger: source, manual: true, force: true })
            return false
        }

        function promptWebAppInstall() {
            const game = hru.value
            return new Promise<BeforeInstallPromptEvent | undefined>((resolve) => {
                onWebAppPrompt = resolve
                setTimeout(() => resolve(undefined), WEB_APP_PROMPT_WAIT_MS)
                webAppManifestGame.value = game
            })
                .then((event) => {
                    onWebAppPrompt = undefined
                    if (!event) {
                        return false
                    }
                    deferredPrompt.value = event
                    return leaveFullscreen().then(() => triggerNativePrompt(GAME_INSTALL_SOURCE))
                })
                .then((accepted) => {
                    // after an install Chrome still needs this manifest to finish it
                    webAppManifestGame.value = accepted ? game : undefined
                    return accepted
                })
        }

        function installFromGame() {
            if (showingNativePWA.value || onWebAppPrompt || navigator.userActivation?.isActive === false) {
                return Promise.resolve(false)
            }
            if (isNativeAppManifest.value) {
                return promptWebAppInstall()
            }
            return hasDeferredPrompt.value
                ? leaveFullscreen().then(() => onInstallPWA(GAME_INSTALL_SOURCE))
                : onInstallPWA(GAME_INSTALL_SOURCE)
        }

        async function onInstallPWAorMSN() {
            const url = msStoreGameIds[hru.value as string]
            if (navigator.userAgentData?.platform === 'Windows' && url) {
                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'ms_store_url_opened',
                })
                window.open(`https://apps.microsoft.com/detail/${url}?mode=full`, '_blank')
            } else {
                await onInstallPWA()
            }
        }

        return {
            displayCount,
            lastDisplayTime,
            installed,
            sessions,
            triggerNativePrompt,
            handleOptOut,
            handleAccepted,
            handleOnShow,
            showPWAInstallPrompt,
            showPWAGameInstallPrompt,
            forceHidePWAInstallPrompt,
            hasDeferredPrompt,
            shouldShowPrompt,
            init,
            initialized,
            isDisplaying,
            install,
            showingNativePWA,
            pwaHasShowed,
            installedGames,
            setInstalled,
            checkInstalled,
            onInstallPWAorMSN,
            webAppManifestGame,
            installFromGame,
            verifiedInstalls,
            homeScreenRewards,
            claimHomeScreenReward,
        }
    },
    {
        persist: {
            key: 'pwa_install',
            pick: [
                'displayCount',
                'lastDisplayTime',
                'installed',
                'sessions',
                'installedGames',
                'verifiedInstalls',
                'homeScreenRewards',
            ],
            afterHydrate: (ctx) => {
                if (ctx.store._storageListenerAdded) {
                    return
                }
                ctx.store._storageListenerAdded = true
                window.addEventListener('storage', (e: StorageEvent) => {
                    if (e.key === ctx.store.$id) {
                        ctx.store.$hydrate()
                    }
                })
            },
        },
    },
)
